import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { parseChatDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql";
import { NavBarItemType, type NavbarSection } from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const addChatMutation = graphql(/* GraphQL */ `
	mutation addChat(
		$section: NavbarSection!
		$name: String
		$chatId: String!
		$parentFolderKey: String
		$beforeKey: String
		$afterKey: String
	) {
		navbar {
			addChat(
				section: $section
				name: $name
				chatId: $chatId
				parentFolderKey: $parentFolderKey
				beforeKey: $beforeKey
				afterKey: $afterKey
			) {
				key
				name
				parentKey
				rank
			}
		}
	}
`);

export interface AddChatToWorkspaceParams {
	section: NavbarSection;
	name?: string | null;
	chatId: string;
	parentFolderKey?: string | null;
	beforeKey?: string | null;
	afterKey?: string | null;
}

export function useAddChatToWorkspace() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			name,
			chatId,
			parentFolderKey,
			beforeKey,
			afterKey,
		}: AddChatToWorkspaceParams) => {
			const response = await client.mutate({
				mutation: addChatMutation,
				variables: {
					section,
					name: name ?? undefined,
					chatId: parseChatDocumentId(chatId).toString(),
					parentFolderKey: parentFolderKey ?? undefined,
					beforeKey: beforeKey ?? undefined,
					afterKey: afterKey ?? undefined,
				},
			});
			return response.data?.navbar?.addChat ?? null;
		},
		onMutate: async (variables) => {
			const queryKey = navbarBranchQueryKeys.branch(
				variables.section,
				variables.parentFolderKey,
			);
			await queryClient.cancelQueries({ queryKey });
			const previousItems =
				queryClient.getQueryData<NavbarBranchItem[]>(queryKey);

			const tempKey = parseChatDocumentId(variables.chatId).toString();
			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
				if (!old) {
					return old;
				}
				const placeholder: NavbarBranchItem = {
					key: tempKey,
					name: variables.name || "New Chat",
					type: NavBarItemType.Chat,
					parentKey: variables.parentFolderKey ?? null,
					rank: "",
					hasChildren: false,
				};
				if (old.some((i) => i.key === tempKey)) {
					return old;
				}
				if (variables.beforeKey) {
					const idx = old.findIndex((i) => i.key === variables.beforeKey);
					if (idx >= 0) {
						return [...old.slice(0, idx), placeholder, ...old.slice(idx)];
					}
				}
				return [placeholder, ...old];
			});

			return { previousItems, queryKey, tempKey };
		},
		onSuccess: (data, variables, context) => {
			if (!data || !context?.tempKey) {
				return;
			}
			const queryKey = navbarBranchQueryKeys.branch(
				variables.section,
				variables.parentFolderKey,
			);
			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
				if (!old) {
					return old;
				}
				const realItem: NavbarBranchItem = {
					key: data.key,
					name: data.name,
					type: NavBarItemType.Chat,
					parentKey: data.parentKey ?? null,
					rank: data.rank,
					hasChildren: false,
				};
				return old.map((i) => (i.key === context.tempKey ? realItem : i));
			});
		},
		onError: (error, _variables, context) => {
			if (context?.queryKey) {
				queryClient.setQueryData(context.queryKey, context.previousItems);
			}
			Sentry.captureException(error);
			handleError(error, "Failed to add chat to Workspace");
		},
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.branch(
					variables.section,
					variables.parentFolderKey,
				),
			});
		},
	});
}
